import { useTranslation } from "@repo/i18n";
import type { VenueConfig } from "../../config/types";
import { formatServiceDate, formatSlotTime } from "../format";
import { manageLink } from "../manageLink";
import { rejectionMessageKey } from "../rejection";
import type { Confirmation } from "../flow/state";

/**
 * CONFIRMED end screen (instant-confirm default, R2): a DISTINCT confirmed headline, the booking
 * summary, "confirmation sent to <email>" copy (sending is not ours, guest-notifications) and the
 * manage action, so a guest who gets no message can still change their booking (BK-12).
 */
export function Confirmed({
  confirmation,
  config,
  locale,
  serviceDay,
  preview,
}: {
  confirmation: Confirmation;
  config: VenueConfig;
  locale: string;
  serviceDay: string;
  preview: boolean;
}) {
  const { t } = useTranslation();
  const summary = t("widget.booking.confirmed.summary", {
    venue: config.venueName,
    date: formatServiceDate(serviceDay, config.timeZone, locale),
    time: formatSlotTime(confirmation.startSec, config.timeZone, locale),
    guests: confirmation.partySize,
  });
  return (
    <section className="rsv-step" aria-label={t("widget.booking.confirmed.title")}>
      <span className="rsv-badge" aria-hidden="true">
        ✓
      </span>
      <h2>{t("widget.booking.confirmed.title")}</h2>
      <p className="rsv-summary">{summary}</p>
      {confirmation.email ? (
        <p>{t("widget.booking.confirmed.email", { email: confirmation.email })}</p>
      ) : null}
      {preview ? null : <ManageAction confirmation={confirmation} />}
    </section>
  );
}

/**
 * PENDING end screen (request-to-book / over-max, R2): a DISTINCT "request sent — awaiting
 * confirmation" headline — the API never says confirmed for a request. The full request lifecycle is
 * M3 (request-to-book); this is the minimal "request sent" state, plus the manage action (the manage
 * page already handles pending bookings).
 */
export function Pending({
  confirmation,
  config,
  locale,
  serviceDay,
  preview,
}: {
  confirmation: Confirmation;
  config: VenueConfig;
  locale: string;
  serviceDay: string;
  preview: boolean;
}) {
  const { t } = useTranslation();
  const summary = t("widget.booking.pending.summary", {
    venue: config.venueName,
    date: formatServiceDate(serviceDay, config.timeZone, locale),
    guests: confirmation.partySize,
  });
  return (
    <section className="rsv-step" aria-label={t("widget.booking.pending.title")}>
      <h2>{t("widget.booking.pending.title")}</h2>
      <p className="rsv-summary">{summary}</p>
      {confirmation.email ? (
        <p>{t("widget.booking.pending.email", { email: confirmation.email })}</p>
      ) : null}
      {preview ? null : <ManageAction confirmation={confirmation} />}
    </section>
  );
}

/**
 * Opens the manage page in a new tab: `/m/*` refuses to be framed, and an island runs on the
 * storefront's origin, so it can't navigate in place. A guest with no email may get no message at all,
 * so they are told to keep the link.
 */
function ManageAction({ confirmation }: { confirmation: Confirmation }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="rsv-actions">
        <a
          className="rsv-button"
          href={manageLink(confirmation.venueId, confirmation.manageToken)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("widget.booking.manage.action")}
        </a>
      </div>
      {confirmation.email ? null : (
        <p className="rsv-hint">{t("widget.booking.manage.keepLink")}</p>
      )}
    </>
  );
}

/**
 * BLOCKED end screen (R5, GU-4/BK-9): the NEUTRAL refusal a `BannedUser` sees — the same generic copy
 * any ordinary refusal has, with NO blacklist vocabulary anywhere. Offers the venue contact so it is
 * never a dead end. Real blacklist evaluation arrives in M5 with zero UI rework.
 */
export function Blocked({ config }: { config: VenueConfig }) {
  const { t } = useTranslation();
  const contact = config.booking?.contact;
  return (
    <section className="rsv-step" role="alert" aria-label={t("widget.booking.blocked.title")}>
      <h2>{t("widget.booking.blocked.title")}</h2>
      <p>{t("widget.booking.blocked.body")}</p>
      <ContactLines phone={contact?.phone} email={contact?.email} />
    </section>
  );
}

/**
 * CONTACT_FALLBACK (online-booking-off / not-live / no-fit edge cases): venue contact details instead
 * of an empty widget. The message is keyed off the rejection so the diner understands why.
 */
export function ContactFallback({
  config,
  rejectionCode,
}: {
  config: VenueConfig;
  rejectionCode: string;
}) {
  const { t } = useTranslation();
  const contact = config.booking?.contact;
  return (
    <section className="rsv-step" aria-label={t("widget.booking.contact.title")}>
      <h2>{t("widget.booking.contact.title")}</h2>
      <p>{t(rejectionMessageKey(rejectionCode))}</p>
      <ContactLines phone={contact?.phone} email={contact?.email} />
    </section>
  );
}

function ContactLines({ phone, email }: { phone?: string; email?: string }) {
  const { t } = useTranslation();
  if (!phone && !email) return null;
  return (
    <div className="rsv-card">
      <span className="rsv-hint">{t("widget.booking.contact.reach")}</span>
      {phone ? <a href={`tel:${phone.replace(/\s/g, "")}`}>{phone}</a> : null}
      {email ? <a href={`mailto:${email}`}>{email}</a> : null}
    </div>
  );
}
